import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import type { TokenCredential } from "@azure/identity";
import { readAppSecrets, signAppJwt } from "@m8t-stack/github-app-auth";
import { LocalCliError } from "./errors.js";
import { readAgentYaml, patchAgentYaml } from "./agent-yaml.js";
import { renderPersonaBody } from "./persona-render.js";
import { stripBrainLoader } from "./brain-loader-block.js";
import { getAgentVersion, type AgentDefinition } from "./foundry-agent-get.js";
import { createAgentVersion } from "./foundry-agent-version.js";

const FOUNDRY_ARM_API = "2025-04-01-preview";
const ARM_SCOPE = "https://management.azure.com/.default";
const GITHUB_APP_API = "https://api.github.com";

export interface UnlinkBrainArgs {
  credential: TokenCredential;
  kvUri: string;
  projectEndpoint: string;   // data-plane URL
  projectArmId: string;      // /subscriptions/.../projects/<proj>
  agentName: string;
  keepAppInstall: boolean;   // skip DELETE /app/installations/{id}
  home?: string;             // for testing — defaults to os.homedir()
  onProgress?: (msg: string) => void;
}

export interface UnlinkBrainResult {
  foundryVersion?: string;
  connectionDeleted: boolean;
  appUninstalled: boolean;
}

/**
 * The convergent unlink step.
 *
 * Caller (CLI command) has already confirmed with the operator.
 * This function performs the cascade unconditionally:
 *
 *   1. Read current agent + parse metadata.brain for installationId + credentialRef.
 *   2. Re-render persona body WITHOUT the brain loader.
 *   3. createVersion with brain-stripped definition + metadata.brain = ""
 *      (Foundry's metadata is Record<string,string> — null is rejected; empty-string accepted).
 *   4. DELETE the Foundry connection. 404 = idempotent success.
 *   5. Unless keepAppInstall=true: DELETE /app/installations/{id} via App JWT. 404 = idempotent.
 *   6. Patch per-agent yaml: brain = null (removes the block).
 *
 * Repo is NOT touched — operator runs `gh repo delete` themselves if needed.
 */
export async function unlinkBrain(args: UnlinkBrainArgs): Promise<UnlinkBrainResult> {
  // eslint-disable-next-line @typescript-eslint/no-empty-function -- noop progress stub
  const progress = args.onProgress ?? ((_m: string) => {});

  progress("Reading current agent definition…");
  const current = await getAgentVersion({
    credential: args.credential,
    projectEndpoint: args.projectEndpoint,
    agentName: args.agentName,
  });

  const existingBrain = parseBrainMeta(current.metadata?.brain);
  if (!existingBrain) {
    throw new LocalCliError({
      code: "BRAIN_NOT_LINKED",
      message: `Agent '${args.agentName}' is not linked to a brain (metadata.brain is absent or invalid).`,
      hint: `Run 'm8t brain link' to establish a link first.`,
    });
  }

  const { installationId, credentialRef } = existingBrain;
  const connectionName = credentialRef;

  progress("Re-rendering persona body (without brain loader)…");
  const yaml = readAgentYaml(args.agentName, args.home);
  let strippedInstructions: string;
  if (yaml?.personaPath) {
    let personaPath = yaml.personaPath;
    if (!personaPath.startsWith("/")) {
      const marker = path.join(args.home ?? os.homedir(), ".m8t", "repo-root");
      if (!fs.existsSync(marker)) {
        throw new LocalCliError({
          code: "PERSONA_PATH_NOT_ABSOLUTE",
          message: `personaPath '${personaPath}' is relative and the repo-root marker is missing.`,
          hint: "Re-run 'm8t install' from a fresh m8t checkout (writes ~/.m8t/repo-root) — or edit the personaPath to absolute.",
        });
      }
      personaPath = path.join(fs.readFileSync(marker, "utf8").trim(), personaPath);
    }
    strippedInstructions = renderPersonaBody(personaPath, yaml.fillableFieldValues ?? {}, args.agentName);
  } else {
    // No persona path (e.g. a brain linked via the deployed-instructions fallback,
    // whose synthesized yaml has personaPath ""): strip the loader block from the
    // deployed instructions instead of re-rendering from a persona file.
    progress("No persona yaml — stripping the loader from the deployed instructions…");
    strippedInstructions = stripBrainLoader(current.definition.instructions ?? "");
  }

  progress("Deploying brain-stripped agent version…");
  const foundryVersion = await createBrainStrippedVersion({
    credential: args.credential,
    projectEndpoint: args.projectEndpoint,
    agentName: args.agentName,
    currentDefinition: current.definition,
    currentMetadata: current.metadata ?? {},
    strippedInstructions,
  });

  progress("Deleting Foundry connection…");
  const connectionDeleted = await deleteConnection({
    credential: args.credential,
    projectArmId: args.projectArmId,
    connectionName,
  });

  let appUninstalled = false;
  if (!args.keepAppInstall) {
    progress(`Uninstalling GitHub App installation ${installationId}…`);
    appUninstalled = await uninstallApp({
      credential: args.credential,
      kvUri: args.kvUri,
      installationId,
    });
  }

  if (yaml) {
    progress("Clearing brain block from per-agent yaml…");
    patchAgentYaml(args.agentName, { brain: null }, args.home);
  }

  return { foundryVersion, connectionDeleted, appUninstalled };
}

// ──── helpers ───────────────────────────────────────────────────────────────

interface BrainMeta {
  installationId: string;
  credentialRef: string;
  [key: string]: unknown;
}

function parseBrainMeta(raw: string | undefined): BrainMeta | undefined {
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (
      typeof parsed.installationId === "string" &&
      typeof parsed.credentialRef === "string"
    ) {
      return parsed as BrainMeta;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

async function createBrainStrippedVersion(args: {
  credential: TokenCredential;
  projectEndpoint: string;
  agentName: string;
  currentDefinition: AgentDefinition;
  currentMetadata: Record<string, string>;
  strippedInstructions: string;
}): Promise<string> {
  // Strip the brain mcp tool; keep everything else.
  const otherTools = (args.currentDefinition.tools ?? []).filter(
    (t) => !(t.type === "mcp" && (t as { server_label?: string }).server_label === "brain"),
  );
  const definition: AgentDefinition = {
    ...args.currentDefinition,
    instructions: args.strippedInstructions,
    tools: otherTools,
  };
  // Empty-string is the correct way to clear a Foundry string metadata field
  // (null is rejected by the API; omitting the key leaves the old value).
  const metadata: Record<string, string> = {
    ...args.currentMetadata,
    brain: "",
  };
  return createAgentVersion({
    credential: args.credential,
    projectEndpoint: args.projectEndpoint,
    agentName: args.agentName,
    definition,
    metadata,
  });
}

async function deleteConnection(args: {
  credential: TokenCredential;
  projectArmId: string;
  connectionName: string;
}): Promise<boolean> {
  const armToken = await args.credential.getToken(ARM_SCOPE);
  if (!armToken?.token) {
    throw new LocalCliError({ code: "ARM_AUTH", message: "Could not acquire ARM token" });
  }
  const url = `https://management.azure.com${args.projectArmId}/connections/${args.connectionName}?api-version=${FOUNDRY_ARM_API}`;
  const res = await fetch(url, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${armToken.token}` },
  });
  // 404 = already gone — idempotent success.
  if (res.ok || res.status === 404) return true;
  const text = await res.text();
  throw new LocalCliError({
    code: "CONN_DELETE_FAILED",
    message: `DELETE ${url}: HTTP ${res.status.toString()}\n${text.slice(0, 300)}`,
  });
}

async function uninstallApp(args: {
  credential: TokenCredential;
  kvUri: string;
  installationId: string;
}): Promise<boolean> {
  const secrets = await readAppSecrets({ credential: args.credential, kvUri: args.kvUri });
  const jwt = signAppJwt({ appId: secrets.appId, privateKeyPem: secrets.privateKeyPem });
  const url = `${GITHUB_APP_API}/app/installations/${args.installationId}`;
  const res = await fetch(url, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${jwt}`,
      Accept: "application/vnd.github+json",
      "X-GitHub-Api-Version": "2022-11-28",
    },
  });
  // 404 = already uninstalled — idempotent success.
  if (res.ok || res.status === 404) return true;
  const text = await res.text();
  throw new LocalCliError({
    code: "APP_UNINSTALL_FAILED",
    message: `DELETE ${url}: HTTP ${res.status.toString()}\n${text.slice(0, 300)}`,
  });
}
